import { AppPricingInterval, CurrentSubscription, Plan, PlanPricing } from "@/api/account/type.d"

export type TempPlanInfo = {
  level: number
  planName: string
  activePrice: PlanPricing
  isLowYearToHighMonth?: boolean
  isHighMonthToLowerYear?: boolean
  isHighYearToMonth?: boolean
  isAPPToBundle?: boolean
  // 当前套餐信息
  currentPlanInterval?: AppPricingInterval
  currentPlanPriceId?: number
  currentPlanLevel?: number
} | null

export type DowngradeConditions = {
  isHighMonthToLowerYear: boolean
  isLowYearToHighMonth: boolean
  isDowngrade: boolean
  isAPPToBundle?: boolean
  shouldShowImportantNotice: boolean
  shouldShowFeatureList: boolean
}

/**
 * 获取降级相关的条件判断
 * @param tempPlanInfo 临时计划信息
 * @param currentSubscription 当前订阅信息
 * @returns 返回各种条件判断结果
 */
export const getDowngradeConditions = (
  tempPlanInfo: TempPlanInfo,
  currentSubscription: CurrentSubscription,
): DowngradeConditions => {
  const isHighMonthToLowerYear = tempPlanInfo?.isHighMonthToLowerYear || false
  const isLowYearToHighMonth = tempPlanInfo?.isLowYearToHighMonth || false
  const isDowngrade = tempPlanInfo ? tempPlanInfo.level < currentSubscription.level : false
  const shouldShowImportantNotice = tempPlanInfo ? (isDowngrade || isLowYearToHighMonth) : false
  const shouldShowFeatureList = tempPlanInfo ? isDowngrade : false

  return {
    isHighMonthToLowerYear,
    isLowYearToHighMonth,
    isDowngrade,
    isAPPToBundle: tempPlanInfo ? tempPlanInfo.isAPPToBundle : false,
    shouldShowImportantNotice,
    shouldShowFeatureList,
  }
}

/**
 * 获取Modal的配置信息
 * @param conditions 条件判断结果
 * @param t 翻译函数
 * @returns Modal配置
 */
export const getModalConfig = (
  conditions: DowngradeConditions,
  t: (key: string) => string,
) => {
  const { isHighMonthToLowerYear, isDowngrade, isAPPToBundle } = conditions

  // app -> bundle 降级，显示change plan标题
  if (isHighMonthToLowerYear || isDowngrade && isAPPToBundle) {
    return {
      title: t("account:ChangePlanReminder"),
      primaryActionContent: t("account:ChangePlan"),
      secondaryActionContent: t("common:Cancel"),
    }
  }

  return {
    title: t("account:DowngradeReminder"),
    primaryActionContent: t("account:DegradeAlert.primaryButtonText"),
    secondaryActionContent: t("common:Cancel"),
  }
}

/**
 * 获取重要提示的文案内容
 * @param conditions 条件判断结果
 * @param currentSubscription 当前订阅信息
 * @param t 翻译函数
 * @returns 提示文案
 */
export const getImportantNoticeText = (
  conditions: DowngradeConditions,
  currentSubscription: CurrentSubscription,
  t: (key: string, options?: any) => string,
): string => {
  const { isLowYearToHighMonth } = conditions

  if (isLowYearToHighMonth) {
    return t("account:LowYearToHighMonth")
  }

  return t("account:DowngradeReminderImportant", { planName: currentSubscription.plan_name })
}

/**
 * 获取功能列表数据
 * @param currentPlan 当前计划
 * @param t 翻译函数
 * @returns 功能列表
 */
export const getFeatureList = (
  currentPlan: Plan | undefined,
  t: (key: string) => string,
): string[] => {
  if (!currentPlan) return []
  return [...currentPlan.features_overview, t("account:ManyMore")]
}
